import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../App";
import { TrendingUp, TrendingDown, IndianRupee, Download, Calendar, BarChart3, Package, PieChart, ArrowRight, FileText, RefreshCw, Users, Clock } from "lucide-react";

const PERIODS = [
  { id: "monthly",   label: "Monthly",   def: 24 },
  { id: "quarterly", label: "Quarterly", def: 12 },
  { id: "yearly",    label: "Yearly",    def: 6  },
];

const fmt = (n) => (n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtSigned = (n) => (n >= 0 ? "+" : "") + (n || 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });

// --- Recurring metrics widget (Razorpay auto-renew subscriptions) ---
function RecurringMetrics() {
  const [m, setM] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/subscriptions`).then(r => setM(r.data)).catch(() => setM(null));
  }, []);
  if (!m) return null;
  const metrics = m.metrics || {};
  return (
    <section className="bg-gradient-to-br from-sky/10 to-brand/10 border border-sky/40 rounded-3xl p-5 shadow-soft" data-testid="recurring-metrics">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sky font-bold inline-flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5"/> Recurring (auto-renew)</div>
          <h2 className="font-heading text-2xl font-extrabold mt-1">Subscription MRR &amp; churn</h2>
        </div>
        <Link to="/app/admin/plans" className="text-xs font-bold text-brand inline-flex items-center gap-1 hover:underline">Manage plans <ArrowRight className="w-3 h-3"/></Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Metric icon={IndianRupee} label="MRR" value={`₹${fmt(metrics.mrr_inr)}`} sub={`₹${fmt(metrics.arr_inr)} ARR`}/>
        <Metric icon={Users} label="Active paying" value={fmt(metrics.active_paying)} sub={`${m.active || 0} total active`}/>
        <Metric icon={TrendingDown} label="Churn (30d)" value={fmt(metrics.churn_30d)} sub={`${m.cancelled || 0} cancelled total`}/>
        <Metric icon={Clock} label="Halted" value={fmt(m.halted)} sub="mandate retry"/>
      </div>
      {(metrics.upcoming_renewals_7d || []).length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wider font-bold text-ink-soft mb-2">Upcoming renewals — next 7 days</div>
          <div className="bg-surface rounded-2xl border border-line divide-y divide-line max-h-60 overflow-y-auto">
            {metrics.upcoming_renewals_7d.map(r => (
              <div key={r.subscription_id} className="px-3 py-2 flex items-center justify-between text-xs">
                <div>
                  <div className="font-bold">{r.user?.name || r.user?.email || "—"}</div>
                  <div className="text-ink-soft text-[10px]">{r.plan_name} · renews {new Date(r.expires_at).toLocaleDateString("en-IN")}</div>
                </div>
                <div className="font-bold inline-flex items-center gap-1">
                  ₹{fmt(r.amount)}
                  {r.auto_renew && <RefreshCw className="w-3 h-3 text-sky" title="Auto-renew via Razorpay"/>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function Metric({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-surface rounded-2xl p-3 border border-line">
      <div className="text-[10px] uppercase tracking-wider font-bold text-ink-soft inline-flex items-center gap-1"><Icon className="w-3 h-3"/> {label}</div>
      <div className="font-heading text-2xl font-extrabold mt-1">{value}</div>
      {sub && <div className="text-[10px] text-ink-soft mt-0.5">{sub}</div>}
    </div>
  );
}

export default function AdminFinancials() {
  const [period, setPeriod] = useState("monthly");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const def = PERIODS.find(p => p.id === period)?.def || 24;
      const r = await axios.get(`${API}/admin/financials`, { params: { period, limit: def } });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.status === 403 ? "Admin only — your account doesn't have access to financials." : "Could not load financials.");
      setData(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [period]);

  const series = data?.series || [];
  const seriesDesc = useMemo(() => [...series].reverse(), [series]);
  const totals = data?.totals_all_time || {};

  const latest = series[series.length - 1] || {};
  const prev = series[series.length - 2] || {};
  const momGrowth = prev.platform_revenue ? ((latest.platform_revenue - prev.platform_revenue) / prev.platform_revenue) * 100 : null;

  // Max for chart scaling
  const maxRev = Math.max(1, ...series.map(s => s.platform_revenue || 0));

  return (
    <div className="space-y-6" data-testid="admin-financials-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold inline-flex items-center gap-1"><BarChart3 className="w-4 h-4"/> Company financials</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Complete PNL & growth</h1>
          <p className="text-ink-soft text-sm">All-time + period-wise revenue, costs, and profit. YoY growth auto-computed.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-full border border-line bg-surface p-1" data-testid="period-picker">
            {PERIODS.map(p => (
              <button key={p.id} data-testid={`period-${p.id}`} onClick={() => setPeriod(p.id)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors ${period === p.id ? "bg-brand text-white" : "text-ink-soft hover:text-ink"}`}>
                {p.label}
              </button>
            ))}
          </div>
          <a href={`${API}/admin/financials/export?period=${period}`} target="_blank" rel="noreferrer" data-testid="csv-export-btn"
            className="rounded-full border border-line px-3 py-2 text-xs font-bold inline-flex items-center gap-1 hover:bg-surface-muted">
            <Download className="w-3 h-3"/> CSV
          </a>
          <a href={`${API}/admin/financials/pdf?period=${period}`} target="_blank" rel="noreferrer" data-testid="pdf-export-btn"
            className="rounded-full bg-ink text-white px-4 py-2 text-xs font-bold inline-flex items-center gap-1.5 hover:bg-brand transition-colors shadow-soft">
            <FileText className="w-3.5 h-3.5"/> CFO Report (PDF)
          </a>
        </div>
      </header>

      {error && (
        <div className="bg-danger/10 border border-danger/30 rounded-2xl px-4 py-3 text-sm font-bold text-danger" data-testid="financials-error">
          {error}
        </div>
      )}

      {/* All-time totals */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="all-time-totals">
        <Stat tone="bg-brand text-white"   label="GMV (all-time)"           v={`₹${fmt(totals.gmv)}`}             sub={`${totals.txn_count || 0} txns`}/>
        <Stat tone="bg-leaf/30 text-ink"   label="Platform revenue"         v={`₹${fmt(totals.platform_revenue)}`} sub="Commissions + concierge"/>
        <Stat tone="bg-sky/15 text-sky"    label="Vendor payable (so far)"  v={`₹${fmt(totals.vendor_payable)}`}/>
        <Stat tone="bg-sun/40 text-ink"    label="Refunds + fees"           v={`₹${fmt((totals.refunds || 0) + (totals.gateway_fees_est || 0))}`}/>
        <Stat tone="bg-ink text-white"     label="Net profit"               v={`₹${fmt(totals.net_profit)}`} sub={momGrowth != null ? `MoM ${fmtSigned(momGrowth)}%` : ""}/>
      </div>

      {/* Donations carve-out (pass-through, NOT revenue) */}
      <div className="bg-surface-muted border border-line rounded-2xl px-4 py-3 text-xs text-ink-soft" data-testid="donations-passthrough">
        <strong className="text-ink">₹{fmt(totals.donations_passthrough)}</strong> donations processed as <strong>100% pass-through to NGOs</strong> — these are not platform revenue.
      </div>

      {/* MRR / ARR / Churn — Razorpay auto-renew subscriptions */}
      <RecurringMetrics/>

      {/* Chart */}
      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid="revenue-chart">
        <header className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold inline-flex items-center gap-2"><TrendingUp className="w-5 h-5 text-brand"/> Platform revenue trend</h2>
          <span className="text-xs text-ink-soft font-bold">{series.length} buckets · latest first below</span>
        </header>
        {loading ? <div className="text-ink-soft text-sm py-6 text-center">Loading…</div> : series.length === 0 ? (
          <div className="text-ink-soft text-sm py-6 text-center">No financial data yet. Once payments flow, you&apos;ll see the breakdown here.</div>
        ) : (
          <div className="flex items-end gap-1 h-44 overflow-x-auto pb-2">
            {series.map(s => {
              const h = Math.max(2, Math.round((s.platform_revenue / maxRev) * 100));
              const profit = s.net_profit;
              return (
                <div key={s.bucket} className="flex flex-col items-center min-w-[50px] flex-1" title={`${s.bucket} · ₹${fmt(s.platform_revenue)}`} data-testid={`bar-${s.bucket}`}>
                  <div className="flex flex-col-reverse items-center w-full">
                    <div className="w-full max-w-[36px] bg-brand rounded-t-md transition-all" style={{ height: `${h}%`, minHeight: 2 }}/>
                  </div>
                  <div className={`text-[10px] font-bold mt-1 ${profit >= 0 ? "text-leaf" : "text-danger"}`}>{profit >= 0 ? "+" : "−"}</div>
                  <div className="text-[10px] text-ink-soft font-bold mt-0.5 truncate w-full text-center">{s.bucket}</div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Detailed table */}
      <section className="bg-surface border border-line rounded-3xl overflow-hidden shadow-soft" data-testid="financials-table">
        <header className="px-5 py-4 border-b border-line">
          <h2 className="font-heading text-xl font-bold inline-flex items-center gap-2"><Calendar className="w-5 h-5 text-brand"/> {PERIODS.find(p => p.id === period)?.label} breakdown</h2>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[840px]">
            <thead className="bg-surface-muted text-[10px] uppercase tracking-wider font-bold text-ink-soft">
              <tr>
                <th className="text-left px-4 py-3">Period</th>
                <th className="text-right px-4 py-3">GMV</th>
                <th className="text-right px-4 py-3">Platform rev</th>
                <th className="text-right px-4 py-3">Concierge</th>
                <th className="text-right px-4 py-3">Vendor payable</th>
                <th className="text-right px-4 py-3">Refunds</th>
                <th className="text-right px-4 py-3">Net profit</th>
                <th className="text-right px-4 py-3">YoY</th>
                <th className="text-right px-4 py-3">Txns</th>
              </tr>
            </thead>
            <tbody>
              {seriesDesc.map(s => (
                <tr key={s.bucket} className="border-t border-line hover:bg-surface-muted/50" data-testid={`row-${s.bucket}`}>
                  <td className="px-4 py-2.5 font-bold">{s.bucket}</td>
                  <td className="px-4 py-2.5 text-right font-mono">₹{fmt(s.gmv)}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold text-brand">₹{fmt(s.platform_revenue)}</td>
                  <td className="px-4 py-2.5 text-right font-mono">₹{fmt(s.concierge_revenue)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-ink-soft">₹{fmt(s.vendor_payable)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-danger">{s.refunds ? `₹${fmt(s.refunds)}` : "—"}</td>
                  <td className={`px-4 py-2.5 text-right font-mono font-bold ${s.net_profit >= 0 ? "text-leaf" : "text-danger"}`}>₹{fmt(s.net_profit)}</td>
                  <td className="px-4 py-2.5 text-right">
                    {s.yoy_growth_pct == null ? <span className="text-ink-soft">—</span> :
                      <span className={`inline-flex items-center gap-0.5 font-bold ${s.yoy_growth_pct >= 0 ? "text-leaf" : "text-danger"}`}>
                        {s.yoy_growth_pct >= 0 ? <TrendingUp className="w-3 h-3"/> : <TrendingDown className="w-3 h-3"/>}
                        {fmtSigned(s.yoy_growth_pct)}%
                      </span>}
                  </td>
                  <td className="px-4 py-2.5 text-right text-ink-soft">{s.txn_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Category breakdown for latest bucket */}
      {latest.by_category && Object.keys(latest.by_category).length > 0 && (
        <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid="category-breakdown">
          <header className="mb-4">
            <h2 className="font-heading text-xl font-bold inline-flex items-center gap-2"><PieChart className="w-5 h-5 text-brand"/> Revenue by category — {latest.bucket}</h2>
          </header>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(latest.by_category).sort((a, b) => b[1].platform_revenue - a[1].platform_revenue).map(([cat, v]) => (
              <div key={cat} className="bg-surface-muted rounded-2xl p-3" data-testid={`cat-${cat}`}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-soft">{cat.replace(/_/g, " ")}</div>
                <div className="font-heading text-xl font-extrabold mt-0.5">₹{fmt(v.platform_revenue)}</div>
                <div className="text-[11px] text-ink-soft mt-0.5">{v.count} txn · ₹{fmt(v.gmv)} GMV</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <Link to="/app/admin/payments" data-testid="back-to-payments" className="inline-flex items-center gap-1 text-xs font-bold text-brand hover:underline">
        Manage commission rates & weekly payouts <ArrowRight className="w-3 h-3"/>
      </Link>
    </div>
  );
}

function Stat({ tone, label, v, sub }) {
  return (
    <div className={`rounded-2xl p-3 ${tone}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</div>
      <div className="font-heading text-xl md:text-2xl font-extrabold mt-0.5">{v}</div>
      {sub && <div className="text-[10px] opacity-80 mt-0.5">{sub}</div>}
    </div>
  );
}
